<template>
    <settings-wrapper title="Space settings" section="Resource mapping">
        <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
                <v-card flat elevation="3" class="ma-1">
                    <v-list three-line color="transparent">
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Credit-based services</v-list-item-title>
                                <v-list-item-subtitle>
                                    All credit-based services are drawing upon this resource pool when they are utilized in this space.
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-card-text class="py-0">
                        <v-select
                            required
                            :items="managerResourcePools"
                            :disabled="fetchingAvailableResourcePools"
                            :loading="fetchingAvailableResourcePools"
                            v-model="resourcePoolIdCredit"
                            item-text="long_id"
                            item-value="rpid"
                            label="Resource pool"
                            outlined
                            dense
                            hide-details></v-select>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            v-if="managerResourcePools.length > 0"
                            color="primary"
                            text
                            :disabled="loading || fetchingAvailableResourcePools"
                            :loading="loading || fetchingAvailableResourcePools"
                            @click="updateResourcePoolForCredit">
                            save
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="4" xl="3">
                <v-card flat elevation="3" class="ma-1">
                    <v-list three-line color="transparent">
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Plan-based services</v-list-item-title>
                                <v-list-item-subtitle>
                                    All plan-based services are drawing upon this resource pool when they are utilized in this space.
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-card-text class="py-0">
                        <v-select
                            required
                            :items="managerResourcePools"
                            :disabled="fetchingAvailableResourcePools"
                            :loading="fetchingAvailableResourcePools"
                            v-model="resourcePoolIdSubscription"
                            item-text="long_id"
                            item-value="rpid"
                            label="Resource pool"
                            outlined
                            dense
                            hide-details></v-select>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            v-if="managerResourcePools.length > 0"
                            color="primary"
                            text
                            :disabled="loading || fetchingAvailableResourcePools"
                            :loading="loading || fetchingAvailableResourcePools"
                            @click="updateResourcePoolForSubscription">
                            save
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="4" xl="3">
                <v-card flat elevation="3" class="ma-1">
                    <v-list three-line color="transparent">
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-title>Large File Storage visibility</v-list-item-title>
                                <v-list-item-subtitle>
                                    Only LFS objects in this resource pool are visible and can be attached to this space.
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-card-text class="py-0">
                        <v-select
                            required
                            :items="managerResourcePools"
                            :disabled="fetchingAvailableResourcePools"
                            :loading="fetchingAvailableResourcePools"
                            v-model="resourcePoolIdLFS"
                            item-text="long_id"
                            item-value="rpid"
                            label="Resource pool"
                            outlined
                            dense
                            hide-details></v-select>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn
                            v-if="managerResourcePools.length > 0"
                            color="primary"
                            text
                            :disabled="loading || fetchingAvailableResourcePools"
                            :loading="loading || fetchingAvailableResourcePools"
                            @click="updateResourcePoolForLFS">
                            save
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </settings-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import eventBus from '@/components/EventBus'
import { isAdminRole } from '@/utils'
import SettingsWrapper from '@/components/SettingsWrapper.vue'

export default {
    name: 'SpaceConfigurationResourceMapping',
    components: {
        SettingsWrapper
    },
    data() {
        return {
            resourcePoolIdCredit: null,
            resourcePoolIdSubscription: null,
            resourcePoolIdLFS: null,
            resourcePoolId: null,
            resourceTypes: [],
            error: false,
            loading: false,
            errorContent: 'AN ERROR HAS OCCURRED'
        }
    },
    async created() {
        if (this.activeOrgId && !this.spacesWithPrimarySnapshots.length) {
            await this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        }
        eventBus.$on('global-dialog-accept', this.switchResourcePool)
        this.loadSpaceResourceContext()
    },
    beforeDestroy() {
        eventBus.$off('global-dialog-accept', this.switchResourcePool)
    },
    methods: {
        ensureAdminAccess() {
            if (!this.activeSpaceId || !this.activeSpaceData) {
                return
            }
            if (this.isSpaceAdmin === false) {
                this.$router.replace({
                    name: 'config-space-general',
                    params: {
                        oid: this.activeOrgId,
                        sid: this.activeSpaceId
                    },
                    query: { ...this.$route.query }
                })
            }
        },
        updateResourcePoolForCredit() {
            this.resourcePoolId = this.resourcePoolIdCredit
            this.resourceTypes = [10, 20, 30, 50]
            this.updateResourcePool()
        },
        updateResourcePoolForSubscription() {
            this.resourcePoolId = this.resourcePoolIdSubscription
            this.resourceTypes = [0, 60, 70]
            this.updateResourcePool()
        },
        updateResourcePoolForLFS() {
            this.resourcePoolId = this.resourcePoolIdLFS
            this.resourceTypes = [90]
            this.updateResourcePool()
        },
        updateResourcePool() {
            this.$store.dispatch('showGlobalDialog', {
                dialogTitle: 'Remapping resources',
                dialogText: `Are you sure you want to remap your resources for this space to <b>${
                    this.availableResourcePools.find(rp => rp.rpid === this.resourcePoolId).long_id
                }</b>?`,
                dialogAction: ['accept', 'dismiss']
            })
        },
        switchResourcePool() {
            if (!this.activeSpaceId) {
                return
            }
            this.loading = true
            this.$axios
                .put(`/resource/${this.resourcePoolId}/map/space/${this.activeSpaceId}/rtypes`, {
                    rtypes: this.resourceTypes
                })
                .then(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Space updated successfully',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                    this.$store.dispatch('spaceStore/fetchMappedResources', this.activeSpaceId)
                })
                .catch(error => {
                    this.error = true
                    this.errorContent = error
                })
                .finally(() => {
                    this.loading = false
                })
        },
        loadSpaceResourceContext() {
            if (!this.activeSpaceId) {
                return
            }
            this.$store.dispatch('spaceStore/fetchMappedResources', this.activeSpaceId)
        }
    },
    computed: {
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapState('userStore', ['fetchingAvailableResourcePools', 'availableResourcePools']),
        ...mapState('spaceStore', ['mappedResources']),
        activeOrgId() {
            return this.$route.params.oid
        },
        activeSpaceId() {
            return this.$route.params.sid
        },
        activeSpaceData() {
            if (!this.activeSpaceId) {
                return null
            }
            return this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.activeSpaceId.toString()) || null
        },
        isSpaceAdmin() {
            return isAdminRole(this.activeSpaceData?.space_role)
        },
        managerResourcePools() {
            return this.availableResourcePools.filter(rp => rp.is_rp_manager === true || rp.rpid === this.resourcePoolIdCredit)
        }
    },
    watch: {
        spacesWithPrimarySnapshots: {
            immediate: true,
            handler(spaces) {
                if (!this.$route.params.sid && Array.isArray(spaces) && spaces.length) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            sid: spaces[0].sid
                        },
                        query: { ...this.$route.query }
                    })
                }
            }
        },
        activeSpaceId: {
            immediate: true,
            handler() {
                this.loadSpaceResourceContext()
                this.ensureAdminAccess()
            }
        },
        activeSpaceData: {
            immediate: true,
            handler() {
                this.ensureAdminAccess()
            }
        },
        mappedResources: {
            immediate: true,
            handler(newVal) {
                if (newVal?.HPC_INTER_COMPUTE && Object.keys(newVal.HPC_INTER_COMPUTE).length) {
                    this.resourcePoolIdCredit = Number(Object.keys(newVal.HPC_INTER_COMPUTE)[0])
                }
                if (newVal?.NORMAL_APP && Object.keys(newVal.NORMAL_APP).length) {
                    this.resourcePoolIdSubscription = Number(Object.keys(newVal.NORMAL_APP)[0])
                }
                if (newVal?.LFS && Object.keys(newVal.LFS).length) {
                    this.resourcePoolIdLFS = Number(Object.keys(newVal.LFS)[0])
                }
            }
        }
    }
}
</script>
